import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import React from 'react';
import { Control, Controller, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import FileUpload from '../../../../shared/components/FileUpload';
import { ImageHandler } from '../../../../shared/components/ImageHandler';
import { ERole } from '../../../../shared/enums/user/ERole';
import { ETheme } from '../../../../shared/enums/user/ETheme';
import { IRefund } from '../../../../shared/interfaces/refund/IRefund';
import { IUser } from '../../../../shared/interfaces/user/IUser';
import { darkPalette, lightPalette } from '../../../../shared/mui/palettes';
import { RootState } from '../../../../store/store';

interface IProps {
  isLoading?: boolean;
  isUpdate?: boolean;
  control: Control<IRefund>;
  watch?: UseFormWatch<IRefund>;
}

const RefundForm = ({ isUpdate, control, isLoading, watch }: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refundStatusType } = useSelector((state: RootState) => state.enums);
  const { userData } = useSelector((state: RootState) => state.auth);
  const { theme } = useSelector(
    (state: RootState) => state.auth.userData,
  ) as IUser;
  const isNormalUser = userData?.role === ERole.USER;
  const isStatusVisible = !isNormalUser && isUpdate;
  return (
    <Card>
      <Box sx={{ margin: '20px 0' }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: isStatusVisible ? 4 : 6 }}>
            <Controller
              name={'title'}
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  error={!!error?.message}
                  helperText={error?.message}
                  value={value ?? ''}
                  onChange={onChange}
                  label={t('general.title')}
                  sx={{ width: '100%' }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: isStatusVisible ? 4 : 6 }}>
            <Controller
              name={'amount'}
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  error={!!error?.message}
                  helperText={error?.message}
                  value={value ?? ''}
                  onChange={onChange}
                  label={t('expenses.price')}
                  sx={{ width: '100%' }}
                  type={'number'}
                />
              )}
            />
          </Grid>
          {isStatusVisible && (
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name={'refundStatus'}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <FormControl fullWidth>
                    <InputLabel
                      className={!!error?.message?.length ? 'Mui-error' : ''}
                      id="status-label"
                    >
                      {t('general.status')}
                    </InputLabel>
                    <Select
                      error={!!error?.message}
                      labelId="status-label"
                      id="status-select"
                      value={value ?? ''}
                      label={t('general.status')}
                      onChange={onChange}
                    >
                      {refundStatusType?.map((status) => {
                        return (
                          <MenuItem key={status} value={status}>
                            {t(`statuses.${status}`)}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {!!error?.message?.length && (
                      <FormHelperText>{error?.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
          )}

          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name={'description'}
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  error={!!error?.message}
                  helperText={error?.message}
                  value={value ?? ''}
                  onChange={onChange}
                  label={t('general.description')}
                  sx={{ width: '100%' }}
                  multiline
                  rows={4}
                />
              )}
            />
          </Grid>
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Controller
              name={'file'}
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => {
                return (
                  <>
                    <FileUpload
                      file={value ?? null}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        onChange(file);
                      }}
                    />
                    {!!error?.message?.length && (
                      <FormHelperText>{error?.message}</FormHelperText>
                    )}
                  </>
                );
              }}
            />
            {watch && watch('refundPhoto')?.path && (
              <ImageHandler
                src={String(watch('refundPhoto')?.path)}
                alt="File preview"
                onClick={() => window.open(watch('refundPhoto')?.path)}
                className={`profile-backup-image ms-3`}
                style={{
                  borderColor:
                    theme === ETheme.LIGHT
                      ? lightPalette.primary.light
                      : darkPalette.background.light,
                }}
              />
            )}
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant={'contained'}
          color={'secondary'}
          sx={{ marginTop: '20px' }}
          type={'button'}
          onClick={() => navigate(-1)}
        >
          {t('button.cancel')}
        </Button>

        <LoadingButton
          loading={isLoading}
          variant={'contained'}
          type={'submit'}
          sx={{ margin: '20px 0 0 10px' }}
        >
          {t(isUpdate ? 'button.update' : 'button.create')}
        </LoadingButton>
      </Box>
    </Card>
  );
};

export default RefundForm;
